import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { LuBox, LuShoppingCart } from 'react-icons/lu';
import { LuX } from 'react-icons/lu';

// COMPONENTES
import Icon from 'components/Atoms/Icons/Icon';

// DS
import {
  Col,
  Row,
  VertButton,
  VertTypography,
  notification,
} from '@vertem-core/vertice-ds';

// STYLES
import 'Assets/styles/default/components/Product/ProductDetailBlockStyles.less';

// UTILS
import { cloudloyalty } from 'Services/Apis/clients';
import { useHierarchyProductsLocalStorageContext } from 'Context/HierarchyLocalStorageContext';
import { useSimulationLocalStorageContext } from 'Context/SimulationLocalSotrageContext';
import ProductDetailAvailability from "./ProductDetailAvailability";
import ProductDetailVoucher from "./ProductDetailVoucher";
import ProductDetailFeatures from "./ProductDetailFeatures";
import ProductDetailRate from "./ProductDetailRate";
import ProductDetailFreight from "./ProductDetailFreight";
import useGA from "Utils/Ga";
import { useRating } from "components/RatingProducts/Provider";
import yn from 'yn';
import ynToBool from 'Utils/ynToBool';

// PRODUCT DETAILS BLOCK COMPONENT
const ProductDetailBlock = ({
  addToCart,
  isAvailable,
  minimumPurchase,
  pointValue,
  regularPointsValue,
  rate,
  skus,
  productInfo,
  isQuoteItem,
  quotationModalHandler,
  vendor,
  inventoryQuantity,
  hasInventory,
  selectedVendor,
  clearSelectedVendor,
  productHaveRelatedItems
}) => {
  /* Hooks */
  const { sku } = useParams();
  const navigate = useNavigate();
  const [skuType, setSkuType] = useState([]);
  const [skuFeatures, setSkuFeatures] = useState([]);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [isAvailableGeneric, setIsAvailableGeneric] = useState(false);

  const { eventGaUnlimited } = useGA()
  const Catalog = useSelector(({ Catalog: data }) => data);
  const { user } = useSelector((state) => state.oidc);
  const Participants = useSelector(({ Participants: data }) => data);

  const { hierarchyProductsLocalStorageValue, setHierarchyLocalStorageValue } =
    useHierarchyProductsLocalStorageContext();
  const { simulationLocalStorageValue } = useSimulationLocalStorageContext();

  /* States */
  const [selectedsFeatures, setSelectedsFeatures] = useState({});

  /* Vars */
  const isVoucher = vendor?.type === 'EXTERNALVOUCHERVIRTUAL';
  const email = Participants?.email?.value || '';

  const onClickFeature = (type, value) => {
    let features = selectedsFeatures;
    features[type] = value;
    const skuSelected = skuType.filter((f) => {
      let response = true;
      Object.keys(f).forEach((k) => {
        if (f[k] && features[k] && features[k] !== f[k]) {
          response = false;
        }
      });

      return response;
    });

    if (!skuSelected.length) {
      const optionsData = skuFeatures.filter(
        ({ type: t, value: v }) => type === t && v === value
      );
      navigate(`/product/${optionsData[0]?.sku}`);
      return;
    }

    navigate(`/product/${skuSelected[0]?.sku}`);
  };


  const handleSetSkuFeatures = useCallback(() => {
    if (skus && skus.length) {
      const items = skus
        .map(({ sku, skuFeatures, images }) =>
          skuFeatures.map((feature) => ({ sku, images, ...feature }))
        )
        .reduce((acc, curr) => [...acc, ...curr], []);
      setSkuFeatures(items);
    }
  }, [skus, skuFeatures]);

  let featuresItems = {};

  skuFeatures &&
    skuFeatures.forEach((item) => {
      if (!featuresItems[item.type]) {
        featuresItems[item.type] = [];
      }
      const itemIncludes = featuresItems[item.type].filter(
        (f) => f.value === item.value
      );
      if (itemIncludes.length === 0) {
        featuresItems[item.type].push({
          label: item.value,
          value: item.value,
          availability: true,
        });
      }
    });

  skuFeatures &&
    skuFeatures.forEach((element) => {
      const index = skuType.findIndex((x) => x.sku === element.sku);
      if (index !== -1) {
        skuType[index][element.type] = element.value;
      }
    });

  /* Lifecycles */
  const isCurrentSku = useCallback(
    (skuToCompare) => {
      return skuToCompare === sku;
    },
    [sku]
  );

  useEffect(() => {
    skuType.forEach((item) => {
      if (item.currentSku) {
        setSelectedsFeatures({ ...item });
      }
    });
  }, [skuType]);

  const clearCart = async (sku) => {
    try {
      await cloudloyalty(user).delete(
        `v1/carts/me/items/${sku}`
      );
      return 'success';
    } catch (error) {
      console.log('clearCart error XXXXXXX ', error);
      throw error;
    }
  };

  const handleAddLocalStorageProduct = async (product) => {

    let listProductsStorage = hierarchyProductsLocalStorageValue?.products
      ?.length
      ? hierarchyProductsLocalStorageValue?.products
      : [];
    try {
      if (listProductsStorage?.length === 0) {
        const cart = await getCart();

        if (cart.data !== null) {
          const cartItems = cart.data.items;

          if (cartItems.length > 0) {
            for (const item of cartItems) {
              await clearCart(item.sku);
            }
          }
        }
      }

      if (listProductsStorage?.find((p) => p.sku === product.sku)) {
        const itemToUpdate = listProductsStorage.find(
          (item) => item.sku === product.sku
        );
        const updatedItem = itemToUpdate
          ? { ...itemToUpdate, quantity: itemToUpdate.quantity + 1 }
          : null;
        const filteredArray = listProductsStorage.filter(
          (item) => item.sku !== product.sku
        );

        setHierarchyLocalStorageValue({
          products: [...filteredArray, updatedItem],
        });
      } else {
        listProductsStorage?.push(product);
        setHierarchyLocalStorageValue({ products: listProductsStorage });
      }
      const productIsGeneric = productInfo.skus[0].sku.includes('-PGS');

      if (!productIsGeneric) {
        await handleAddCart(product);
      }
    } catch (err) {
      throw err;
    }
  };

  const getCart = async () => {
    try {
      const response = await cloudloyalty(user).get(`v1/carts/me`);
      return { error: false, data: { ...response.data } };
    } catch (error) {
      console.log('getCart error XXXXXXX ', error);
      throw error;
    }
  };

  const addItemToCart = async (data) => {
    try {
      const response = await cloudloyalty(user).post(`v1/carts/me/items`, data);
      return { error: false, data: { ...response.data } };
    } catch (error) {
      if (error.response.data.messages[0].description.includes('e-mail')) {
        console.log('addItemToCart error XXXXXXX ', error);
      } else {
        notification.error({
          message: error.response.data.messages[0].description.inclu,
        });
      }
      throw error;
    }
  };

  const handleAddCart = async (product) => {
    const data = {
      vendorId: product.vendorId,
      sku: productInfo.skus[0].sku,
      quantity: product.quantity,
    };

    try {
      await addItemToCart(data);
    } catch (error) {
      throw error;
    }
  };

  const addProductToRedemption = async () => {
    setIsAddingProduct(true);
    const path = window.location.pathname;
    const skuFromUrl = path.split('/')[2];
    const product = {
      name: productInfo.name,
      unitPrice: pointValue,
      quantity: productInfo.skus[0].minimumPurchase,
      vendorId: productInfo.vendor.id,
      originalId: productInfo.originalProductId,
      sku: skuFromUrl
    };

    try {
      notification.success({
        message: 'Produto adicionado à solicitação.',
        description:
          'Esse produto foi adicionado à solicitação de Resgate Hierarquia.',
        btn: (
          <VertButton
            type="highlight"
            onClick={() => navigate('/spot-redemption')}
          >
            Visualizar itens
          </VertButton>
        ),
      });
      await handleAddLocalStorageProduct(product);
      setIsAddingProduct(false);
    } catch (err) {
      console.warn(JSON.stringify(err));
      setIsAddingProduct(false);
    }
    return '-';
  };

  const GaTriggerEvent = () => {
    eventGaUnlimited({
      event: "add_to_cart",
      currency: Catalog?.currencyConfiguration?.pointsName?.toUpperCase(),
      value: pointValue,
    }, {
      items: [
        {
          item_id: sku,
          item_name: productInfo.name,
          currency: Catalog?.currencyConfiguration?.pointsName?.toUpperCase(),
          item_brand: vendor.name,
          price: pointValue,
          quantity: 1
        }
      ]
    })
  }

  useEffect(() => {
    if (skus && skus.length) {
      handleSetSkuFeatures();
    }
  }, [skus]);

  useEffect(() => {
    skuFeatures.forEach((element) => {
      const index = skuType.findIndex((x) => x.sku === element.sku);
      if (index === -1 && isCurrentSku(element.sku)) {
        const items = [];
        items.push({
          ...element,
          currentSku: isCurrentSku(element.sku),
        });
        setSkuType(items);
      }
    });
  }, [skuFeatures, isCurrentSku]);

  /* Renders */

  return (
    <div className="pdp-detail-block">
      {isQuoteItem ? (
        <>
          <VertTypography.Title className="pdp-quote-item-title">
            Faça uma cotação
          </VertTypography.Title>
        </>
      ) : (
        <>
          <ProductDetailAvailability
            originalId={productInfo?.originalProductId}
            vendorId={productInfo?.vendor?.id}
            isAvailable={isAvailable}
            pointValue={pointValue}
            regularPointsValue={regularPointsValue}
            isAvailableGeneric={available => { setIsAvailableGeneric(available) }}
          />
        </>
      )}

      {rate && (
        <ProductDetailRate
          rate={rate}
        />
      )}

      {minimumPurchase > 1 && (
        <Row
          justify="center"
          align="middle"
          className="product-qty-min vert-bg-color-brand padding-y-extra-small padding-x-extra-small margin-top-small"
          gutter={0}
        >
          <Col className="col-auto padding-left-extra-small">
            <Icon
              aria-hidden
              className="lucide color-neutral-dark txt-profile-arrow-icon"
              size="18px"
              component={LuShoppingCart}
            />
          </Col>
          <Col className="col padding-left-extra-small">
            <VertTypography.Paragraph className="txt-w-500">{`Resgate mínimo: ${minimumPurchase} itens`}</VertTypography.Paragraph>
          </Col>
        </Row>
      )}

      {ynToBool(process.env.REACT_APP_HAS_INVENTORY_PRODUCT_GS) && hasInventory && (
        <Row
          justify="center"
          align="middle"
          className="product-qty-min vert-bg-color-brand padding-y-extra-small padding-x-extra-small margin-top-small"
          gutter={0}
        >
          <Col className="col-auto padding-left-extra-small">
            <Icon
              aria-hidden
              className="lucide color-neutral-dark txt-profile-arrow-icon"
              size="20px"
              component={LuBox}
            />
          </Col>
          <Col className="col padding-left-extra-small">
            <VertTypography.Paragraph className="txt-w-500">{`Estoque disponível: ${inventoryQuantity}`}</VertTypography.Paragraph>
          </Col>
        </Row>
        )}
        

      <ProductDetailFeatures
        featuresItems={featuresItems}
        selectedsFeatures={selectedsFeatures}
        onClickFeature={onClickFeature}
      />

      {isQuoteItem ? (
        <>
          <div className="divider" />

          <div className="buttons-wrapper">
            <VertButton
              type="highlight"
              onClick={() => quotationModalHandler(true)}
            >
              Solicitar Cotação
            </VertButton>
          </div>
        </>
      ) : (
        <>
         {!yn(process.env.REACT_APP_HIDE_FREIGHT) && <div className="divider" />}

          {isVoucher && (<ProductDetailVoucher email={email} />)}
          {!isVoucher &&  !yn(process.env.REACT_APP_HIDE_FREIGHT) && (
            <ProductDetailFreight />
          )}
        </>
      )}

      <div className="divider" />

      {(isAvailable || isAvailableGeneric) && simulationLocalStorageValue?.enabled ? (
        <VertButton
          onClick={addProductToRedemption}
          disabled={!isAvailable}
          type="highlight"
          loading={isAddingProduct}
        >
          ADICIONAR À SOLICITAÇÃO
        </VertButton>
      ) : (
        (isAvailable || isAvailableGeneric) && !isQuoteItem && (
          <div className="buttons-wrapper">
            <VertButton
              type="detached"
              onClick={() => {
                addToCart({ redirectToCart: true })
                GaTriggerEvent()
              }}
            >
              Resgatar agora
            </VertButton>
            <VertButton
              onClick={() => {
                addToCart({ redirectToCart: false });
                GaTriggerEvent();
                notification.success({
                  message: 'Produto adicionado ao carrinho!',
                });
              }}
            >
              Adicionar ao carrinho
            </VertButton>
          </div>
        )
      )}
    </div>
  );
};

ProductDetailBlock.propTypes = {
  inventoryQuantity: PropTypes.number,
  hasInventory: PropTypes.bool,
  colorOptions: PropTypes.array,
  vendor: PropTypes.object,
  openModalHandler: PropTypes.func,
  pointValue: PropTypes.number,
  regularPointsValue: PropTypes.number,
  rate: PropTypes.number,
  shipping: PropTypes.object,
  sizeOptions: PropTypes.array,
  voltageOptions: PropTypes.array,
  isAvailable: PropTypes.bool,
  addToCart: PropTypes.func,
  minimumPurchase: PropTypes.number,
  productInfo: PropTypes.object,
};

export default ProductDetailBlock;
